/**
 * Thor brand color tokens — single source of truth.
 *
 * Core
 *   --color-primary       #162050  Navy — headlines, nav, dark cards
 *   --color-primary-deep  #0B1230  Gradient depth on dark sections
 *   --color-secondary     #DFFE02  CTAs, highlights (same as new)
 *   --color-paper         #eef0f7  Soft surfaces / page wash
 *
 * Status
 *   active    #3F7A1F
 *   failed    #B3312F
 *   pending   #B57A1D
 *   info      #2E4C9B
 *   inReview  #6B4FB8
 *   new       #DFFE02
 *   draft     #4B5170
 *   archived  #8A8A82
 *
 * Risk (score bands)
 *   low       85+     #DFFE02
 *   average   75–84   #3F7A1F
 *   high      65–74   #B57A1D
 *   veryHigh  <65     #B3312F
 *
 * CSS mirrors: assets/scss/style.scss (:root --color-*)
 * SCSS theme:  assets/scss/_themes-vars.module.scss
 */

export const colors = {
  // Core
  primary: '#162050',
  primaryDeep: '#0B1230',
  secondary: '#DFFE02',
  paper: '#eef0f7',
  surface: '#ffffff',

  // Status / semantic
  active: '#3F7A1F',
  failed: '#B3312F',
  pending: '#B57A1D',
  info: '#2E4C9B',
  inReview: '#6B4FB8',
  new: '#DFFE02',
  draft: '#4B5170',
  archived: '#8A8A82'
};

const normalizeHex = (hex) => {
  const raw = String(hex || '')
    .trim()
    .replace('#', '');
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return raw
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return raw;
  return null;
};

export const hexRgb = (hex) => {
  const raw = normalizeHex(hex);
  if (!raw) return null;
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16)
  };
};

const srgbToLinear = (channel) => {
  const s = channel / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance (0 = black, 1 = white) */
export const relativeLuminance = (hex) => {
  const rgb = hexRgb(hex);
  if (!rgb) return 0;
  return 0.2126 * srgbToLinear(rgb.r) + 0.7152 * srgbToLinear(rgb.g) + 0.0722 * srgbToLinear(rgb.b);
};

/**
 * True when black text has better contrast on `hex` than white text.
 * 0.179 is where contrast vs black equals contrast vs white.
 */
export const isLightColor = (hex) => relativeLuminance(hex) > 0.179;

/** Black or white (or custom pair) for text sitting on a solid `hex` fill */
export const contrastOn = (hex, dark = '#000000', light = '#ffffff') =>
  isLightColor(hex) ? dark : light;

/** Soft tint for badge / chip backgrounds */
export const tint = (hex, amount = 0.14) => {
  const rgb = hexRgb(hex);
  if (!rgb) return colors.paper;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${amount})`;
};

/** Text on a solid status fill — navy on lime, white otherwise */
export const onSolid = (hex) => {
  const h = String(hex || '').toUpperCase();
  if (h === colors.secondary.toUpperCase() || h === colors.new.toUpperCase()) {
    return colors.primary;
  }
  return colors.surface;
};

/** Badge tone: light background + dark lettering (+ solid dot) */
export const statusTone = (hex) => {
  const h = String(hex || '').toUpperCase();
  const isLime = h === colors.secondary.toUpperCase() || h === colors.new.toUpperCase();
  return {
    bg: isLime ? tint(colors.secondary, 0.35) : tint(hex, 0.16),
    text: isLime ? colors.primary : hex,
    dot: hex
  };
};

/** MUI Chip / badge sx from a brand hex */
export const chipToneSx = (hex) => {
  const tone = statusTone(hex);
  return {
    bgcolor: `${tone.bg} !important`,
    color: `${tone.text} !important`,
    fontWeight: 600
  };
};

/** Named status → brand token */
export const status = {
  active: colors.active,
  failed: colors.failed,
  pending: colors.pending,
  info: colors.info,
  inReview: colors.inReview,
  new: colors.new,
  draft: colors.draft,
  archived: colors.archived
};

/** Risk level accent colors (bars / dots); badges use riskTone*) */
export const RISK_COLORS = {
  low: colors.secondary, // #DFFE02
  average: colors.active, // #3F7A1F
  medium: colors.active, // alias of average
  high: colors.pending, // #B57A1D
  veryHigh: colors.failed, // #B3312F
  none: colors.archived
};

export const riskToneFromScore = (score) => {
  if (score === null || score === undefined) return statusTone(RISK_COLORS.none);
  if (score >= 85) return statusTone(RISK_COLORS.low);
  if (score >= 75) return statusTone(RISK_COLORS.average);
  if (score >= 65) return statusTone(RISK_COLORS.high);
  return statusTone(RISK_COLORS.veryHigh);
};

export const riskLabel = (score) => {
  if (score === null || score === undefined) return 'No score';
  if (score >= 85) return 'Low Risk';
  if (score >= 75) return 'Average Risk';
  if (score >= 65) return 'High Risk';
  return 'Very High Risk';
};

export const riskColor = (score) => {
  if (score === null || score === undefined) return RISK_COLORS.none;
  if (score >= 85) return RISK_COLORS.low;
  if (score >= 75) return RISK_COLORS.average;
  if (score >= 65) return RISK_COLORS.high;
  return RISK_COLORS.veryHigh;
};

/** Map bureau / label risk class → risk accent hex */
export const riskClassHex = (riskClass) => {
  if (!riskClass) return RISK_COLORS.none;
  const upper = String(riskClass).toUpperCase();
  if (upper.includes('VERY HIGH') || upper.includes('SEVERE') || ['D', 'E', 'F'].includes(upper)) {
    return RISK_COLORS.veryHigh;
  }
  if (upper.includes('LOW') || upper.includes('GOOD') || upper === 'A') return RISK_COLORS.low;
  if (upper.includes('AVERAGE') || upper.includes('MEDIUM') || upper.includes('MODERATE') || upper === 'B') {
    return RISK_COLORS.average;
  }
  if (upper.includes('HIGH') || upper.includes('POOR') || upper === 'C') return RISK_COLORS.high;
  return RISK_COLORS.none;
};

export const riskChipSxFromClass = (riskClass) => chipToneSx(riskClassHex(riskClass));

export default colors;
